// The operator's two operations over a customer tenant's FORGOTTEN people (owner's request 4.11.2026,
// task 77dd58bf): list them, and delete one of their rows permanently. Mirrors
// `PlatformTenantEndpoints`' two routes under `/api/platform/tenants/{id}/forgotten-users`, gated on the
// platform surface server-side, so a 403 surfaces as an `ApiError` here.
//
// Nothing is hand-declared: both shapes come straight from the generated contract (F-2).

import type { components } from "@clms/contracts";
import { apiFetch } from "@web/lib/api";

/**
 * One crypto-shredded person of the tenant, as the console lists them (`ForgottenUserDto`).
 *
 * It carries no name and cannot: the erasure destroyed the Transit key, so `pseudonym` is derived from the
 * row's own id. `purgeable` is the BACKEND's reading of the condition — `erasureComplete` and nothing
 * retained — and the console branches on it rather than re-deriving it, so the button and the endpoint
 * cannot disagree about who may be deleted.
 */
export type ForgottenUser = components["schemas"]["ForgottenUserDto"];

/** One class of record that holds a row (`RetainedRecord`): its kind, how many rows, and why. */
export type RetainedRecord = components["schemas"]["RetainedRecord"];

/**
 * `GET /api/platform/tenants/{id}/forgotten-users` — oldest erasure first.
 *
 * NOT read when the tenant page opens. It discloses no name (there is none left to disclose), so the
 * reason is the other one the page's load comment gives for the role holders: choosing a tenant is not
 * choosing to look at the erasures somebody ran in it, and the list is one request on a section nobody
 * opens most days. It runs when the section is opened.
 */
export function listForgottenUsers(
  tenantId: string,
  signal?: AbortSignal,
): Promise<ForgottenUser[]> {
  return apiFetch<ForgottenUser[]>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/forgotten-users`,
    { signal },
  );
}

/**
 * `DELETE /api/platform/tenants/{id}/forgotten-users/{userId}` — irreversible, and 204 on success.
 *
 * Every refusal is a 409 whose `error` code says which one (`forgottenUsers.ts`'s `purgeErrorKey`); a 404
 * is an unknown tenant or a person who is not of it. No body either way, so there is nothing to parse: the
 * list is re-read afterwards, which is also what proves the row is gone.
 */
export function purgeForgottenUser(
  tenantId: string,
  userId: string,
  signal?: AbortSignal,
): Promise<void> {
  return apiFetch<void>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/forgotten-users/${encodeURIComponent(userId)}`,
    { method: "DELETE", signal },
  );
}
